import React from "react";
import PageLayout from "../../../components/Layout/PageLayout";

const MOU: React.FC = () => {
  const termsContent = [
    {
      title: "Memorandum of Understanding ('MOU')",
      content: (
        <>
          <div className="text-center border-b border-[gray]/30 pb-0 mb-4"></div>

          {/* 1. Purpose */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">1. Purpose</h2>
            <p className="mb-4">
              This Memorandum of Understanding ("MOU") sets out the mutual
              understanding between the parties for the delivery of professional
              and technology-driven HR services, including setup,
              implementation, ongoing support, and AI-driven recruitment, as
              defined in the approved proposal or project plan.
            </p>
          </section>

          {/* 2. Scope of Work */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">2. Scope of Work</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                25th & Staffing Limited will provide services as outlined in the
                project documentation, proposal, or statement of work (SOW).
              </li>
              <li>
                The Client shall provide timely feedback, access, and approvals
                necessary to meet agreed timelines.
              </li>
            </ul>
          </section>

          {/* 3. Payment and Subscription Terms */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">
              3. Payment and Subscription Terms
            </h2>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Integration Option
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  The Client shall pay one hundred percent (100%) of the total integration fee in advance, upon execution of this Agreement.
                </li>
                <li>
                  Integration work will commence only after full payment has been received and confirmed.
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Customization Option
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  The Client shall pay one hundred percent (100%) of the total customization fee in advance, upon execution of this Agreement.
                </li>
                <li>
                  Customization work will commence only after full payment has been received and confirmed.
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Subscription Option
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Monthly or annual subscription fees are payable in advance prior to activation or renewal of services.
                </li>
                <li>
                  Subscription fees cover platform access, product updates, and standard technical support for the duration of the active subscription term.
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Discount Incentive
              </h3>
              <p>
                A five percent (5%) discount applies when the Client elects to
                make full payment upfront at the time of executing this
                Agreement.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Currency & Exchange
              </h3>
              <p>
                All international payments shall be made in the agreed currency
                at the prevailing exchange rate applicable at the time of
                payment, exclusive of bank or transfer fees, which remain the
                responsibility of the client.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Subscription Renewal and Data Retention
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Subscriptions automatically renew unless canceled before the renewal date.
                </li>
                <li>
                  Access to the platform shall be suspended immediately upon non-payment of subscription fees. All client data will be permanently deleted after thirty (30) calendar days of non-payment. Data deletion is irreversible and performed to ensure system security and compliance.
                </li>
                <li>
                  No Reactivation Fee applies to subscriptions; once data is
                  deleted, it cannot be restored or retrieved. Any subsequent
                  subscription will be treated as a new account setup.
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Refund Policy
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Payments made for integration, customization, or subscription
                  services are non-refundable once the project has commenced or
                  access has been activated.
                </li>
                <li>
                  Any change in project scope or Client-initiated suspension
                  will require a mutual review and may result in adjusted
                  timelines and costs.
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Expedited Implementation
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Standard delivery timelines range between three (3) to four (6) weeks, depending on project scope.
                </li>
                <li>
                  For expedited delivery (two weeks or less), a minimum of thirty percent (30%) surcharge shall apply and must be approved in writing prior to project kickoff.
                </li>
              </ul>
            </div>
          </section>

          {/* 4. Intellectual Property, Platform Use, and Data */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">
              4. Intellectual Property, Platform Use, and Data
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                All proprietary tools, software, frameworks, and templates
                remain the exclusive property of 25th & Staffing Limited.
              </li>
              <li>
                Deliverables created specifically for the Client are licensed
                for internal use only after full payment.
              </li>
              <li>
                The Client may not copy, reproduce, modify, reverse-engineer, or
                redistribute any proprietary material without written consent.
              </li>
              <li>
                All client data remains the Client's property but will be
                handled by 25th & Staffing under strict confidentiality and data
                protection practices.
              </li>
              <li>
                Any violation of IP, platform, or data terms constitutes a
                material breach. 25th & Staffing may suspend access, terminate
                the agreement, and seek damages or injunctive relief.
              </li>
            </ul>
          </section>

          {/* 5. AI Platform Use and Sourcing Fees */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">
              5. AI Platform Use and Sourcing Fees
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Use of the AI Recruiter Platform is limited to the Client's
                internal recruitment activities.
              </li>
              <li>
                The Client may not resell, sublicense, or allow third-party
                access to the system.
              </li>
              <li>
                Any candidate sourced or assessed via 25th & Staffing remains
                subject to sourcing fees, even if hired independently.
              </li>
              <li>
                Any breach of sourcing or non-circumvention obligations shall
                entitle 25th & Staffing Limited to the full placement fee or an
                amount equal to 25–50% of the candidate's first-year
                compensation.
              </li>
            </ul>

            <div className="mt-4">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Exceptions
              </h3>
              <p>
                Candidates independently known or actively under consideration
                by the Client prior to platform identification or sourcing
                engagement must be documented in writing to avoid fee liability.
              </p>
            </div>
          </section>

          {/* 6. Resource Ownership and Non-Poaching */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">
              6. Resource Ownership and Non-Poaching
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                All personnel, consultants, or contractors assigned by 25th &
                Staffing Limited remain employees or agents of the company.
              </li>
              <li>
                The Client shall not directly or indirectly solicit, employ, or engage any resource involved in the project.
              </li>
              <li>
                Breaches will attract damages equivalent to 25–50% of the
                resource's annualized compensation.
              </li>
              <li>
                The client shall not solicit, recruit, hire, or engage any staff or candidate introduced by 25th and Staffing for 24 months after the project ends. 
              </li>
              <li>Direct hiring is permitted only with prior written consent and payment of an agreed fee to 25th and Staffing. </li>
              <li>Any breach makes the client liable for the placement fee and applicable legal damages.</li>
            </ul>
          </section>

          {/* 7. Risk & Responsibility */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">
              7. Risk & Responsibility
            </h2>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.1 Limitation of Liability
              </h3>
              <p>
                25th & Staffing Limited shall not be liable for any indirect,
                incidental, or consequential losses arising from delays, system
                dependencies, third-party integrations, or other factors beyond
                its reasonable control. Liability for direct damages shall be
                limited to the total fees paid by the Client under this
                Agreement.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.2 Limitation of Liability – Client Systems and Data
              </h3>
              <p>
                25th & Staffing Limited shall not be responsible for any loss,
                corruption, breach, or failure of the Client's own systems,
                networks, databases, or IT infrastructure. The Client
                acknowledges that 25th & Staffing's responsibility is limited to
                the services, platform access, and deliverables provided under
                this Agreement.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.3 Force Majeure
              </h3>
              <p>
                Neither party shall be held liable for failure or delay in
                performing obligations due to circumstances beyond its
                reasonable control, including acts of God, natural disasters,
                pandemics, strikes, civil unrest, government actions, or network
                failures. The affected party shall notify the other in writing
                as soon as reasonably practicable and take all reasonable steps
                to resume performance promptly.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.4 Business Continuity and Data Recovery
              </h3>
              <p>
                25th & Staffing Limited maintains a structured Business
                Continuity and Data Recovery Plan to minimize disruption in the
                event of system failure, cyberattack, or operational
                interruption. Backup systems and alternative hosting
                environments shall be activated promptly to restore
                functionality. Clients will receive updates regarding incident
                status and restoration progress until normal operations resume.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.5 Indemnification
              </h3>
              <p>
                The Client agrees to indemnify, defend, and hold harmless 25th &
                Staffing Limited, its officers, directors, employees, and agents
                from and against any and all claims, liabilities, losses,
                damages, costs, or expenses, including reasonable legal fees,
                arising out of or in connection with:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>
                  The Client's misuse of the AI Recruiter platform or other
                  services;
                </li>
                <li>
                  Any breach by the Client of its obligations under this
                  Agreement;
                </li>
                <li>
                  Any violation of applicable laws, regulations, or third-party
                  rights; or
                </li>
                <li>
                  Any claim arising from the Client's own systems, networks,
                  data, or internal processes.
                </li>
              </ul>
              <p className="mt-2">
                This indemnification obligation shall survive the termination or
                expiration of this Agreement.
              </p>
            </div>
          </section>

          {/* 8. Confidentiality */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">8. Confidentiality</h2>
            <p>
              Both parties shall maintain strict confidentiality regarding
              project data, IP, methodologies, candidates, and materials
              exchanged. Disclosure to third parties requires prior written
              consent.
            </p>
          </section>

          {/* 9. Termination */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">9. Termination</h2>
            <p>
              Either party may terminate this MOU with written notice if the
              other materially breaches its obligations and fails to remedy
              within seven (7) days. All completed work and outstanding invoices
              remain payable immediately.
            </p>
          </section>

          {/* 10. Dispute Resolution */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">
              10. Dispute Resolution
            </h2>
            <p>
              Any dispute arising from this MOU shall first be addressed through
              good faith negotiations between senior representatives. If
              unresolved within thirty (30) days, disputes may be referred to
              mediation or arbitration in Lagos, Nigeria, prior to litigation.
            </p>
          </section>

          {/* 11. Amendments */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">11. Amendments</h2>
            <p>
              Any amendments, variations, or additional work outside this MOU
              must be agreed in writing and signed by both parties.
            </p>
          </section>

          {/* 12. Notices */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">12. Notices</h2>
            <p>
              All notices or communications under this MOU must be in writing
              and delivered via email or courier to the designated contact
              persons of both parties.
            </p>
          </section>

          {/* 13. Entire Agreement / Severability */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">
              13. Entire Agreement / Severability
            </h2>
            <p>
              This MOU constitutes the entire agreement between the parties. If
              any provision is held invalid, the remainder shall remain in full
              force and effect.
            </p>
          </section>

          {/* 14. Governing Law */}
          <section className="mb-5">
            <h2 className="text-xl text-gray-900 mb-4">14. Governing Law</h2>
            <p>
              This MOU shall be governed by the laws of the Federal Republic of
              Nigeria, with exclusive jurisdiction vested in the courts of Lagos
              State.
            </p>
          </section>
        </>
      ),
    },
  ];

  return (
    <PageLayout>
      <div className="w-full">
        <div className="bg-white rounded-[10px] shadow-sm mb-5">
          <div className="text-center py-3">
            <h2 className="mb-2 text-[20px] md:text-[35px] font-[500] text-center">
              {termsContent?.map((item) => item?.title)}
            </h2>
          </div>
          <div className="text-gray-700 leading-relaxed">
            {termsContent?.map((item) => item?.content)}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default MOU;
